/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { FC, SVGProps } from 'react';

type AbrNavIconProps = SVGProps<SVGSVGElement> & {
  size?: number | string;
};

const resolveSize = (size: AbrNavIconProps['size']) => {
  if (typeof size === 'number') {
    return size;
  }
  return 16;
};

/** 工作台 · 线性 */
export const IconAbrWorkbench: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <rect
        x="1.5"
        y="1.5"
        width="5.5"
        height="5.5"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <rect
        x="9"
        y="1.5"
        width="5.5"
        height="5.5"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <rect
        x="1.5"
        y="9"
        width="5.5"
        height="5.5"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <rect
        x="9"
        y="9"
        width="5.5"
        height="5.5"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="1.25"
      />
    </svg>
  );
};

/** 工作台 · 实心（选中态） */
export const IconAbrWorkbenchFill: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <rect
        x="1.5"
        y="1.5"
        width="5.5"
        height="5.5"
        rx="1.25"
        fill="currentColor"
      />
      <rect
        x="9"
        y="1.5"
        width="5.5"
        height="5.5"
        rx="1.25"
        fill="currentColor"
        opacity="0.45"
      />
      <rect
        x="1.5"
        y="9"
        width="5.5"
        height="5.5"
        rx="1.25"
        fill="currentColor"
        opacity="0.45"
      />
      <rect
        x="9"
        y="9"
        width="5.5"
        height="5.5"
        rx="1.25"
        fill="currentColor"
      />
    </svg>
  );
};

/** 资源库 · 线性 */
export const IconAbrLibrary: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <path
        d="M2 4.5h12a1 1 0 0 1 1 1v1.5H1V5.5a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <path
        d="M1 7h14v5.5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V7Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 4.5V3.25A1.25 1.25 0 0 1 6.75 2h2.5A1.25 1.25 0 0 1 10.5 3.25V4.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
};

/** 资源库 · 实心（选中态） */
export const IconAbrLibraryFill: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <path
        d="M2 4.5h12a1 1 0 0 1 1 1v1.5H1V5.5a1 1 0 0 1 1-1Z"
        fill="currentColor"
        opacity="0.55"
      />
      <path
        d="M1 7h14v5.5a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V7Z"
        fill="currentColor"
      />
      <path
        d="M5.5 4.5V3.25A1.25 1.25 0 0 1 6.75 2h2.5A1.25 1.25 0 0 1 10.5 3.25V4.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
};

/** 应用商店 · 线性（storefront，雨棚略压低减少头重脚轻） */
export const IconAbrAppStore: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <path
        d="M1.5 8h13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M1.5 8 8 3.75 14.5 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M2 8.25v5.5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M6.75 10.75h2.5v3.75"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/** 应用商店 · 实心（选中态） */
export const IconAbrAppStoreFill: FC<AbrNavIconProps> = ({
  size = 16,
  className,
  style,
  ...rest
}) => {
  const px = resolveSize(size);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      style={style}
      aria-hidden
      {...rest}
    >
      <path
        d="M1.5 8 8 3.75 14.5 8V8.75H1.5V8Z"
        fill="currentColor"
        opacity="0.55"
      />
      <path
        d="M2 8.75h12v5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-5Z"
        fill="currentColor"
      />
    </svg>
  );
};
